import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import styles from "./Menu.module.css";

export type MenuItem = {
  label: string;
  onSelect: () => void;
  /** `danger` — необратимое действие, например «Удалить». */
  tone?: "default" | "danger";
};

type MenuProps = {
  /** Подпись кнопки и самого меню для скринридера. */
  label: string;
  /** Содержимое кнопки, обычно иконка. */
  trigger: ReactNode;
  items: MenuItem[];
  disabled?: boolean;
  className?: string;
};

/**
 * Выпадающее меню у кнопки (отдельного макета нет; карточка сообщения, Figma 627:4903 — кнопка «…»).
 * Закрывается выбором пункта, Escape и тапом мимо; стрелки переводят фокус между пунктами.
 */
export function Menu({ label, trigger, items, disabled = false, className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    root?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const closeOutside = (event: PointerEvent) => {
      if (!root?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    // Tab уводит фокус дальше по странице как обычно, меню просто закрывается.
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    const menuItems = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')];
    const current = menuItems.indexOf(document.activeElement as HTMLElement);
    const focus = (index: number) => menuItems[(index + menuItems.length) % menuItems.length]?.focus();
    if (event.key === "Escape") close();
    else if (event.key === "ArrowDown") focus(current + 1);
    else if (event.key === "ArrowUp") focus(current <= 0 ? -1 : current - 1);
    else if (event.key === "Home") focus(0);
    else if (event.key === "End") focus(-1);
    else return;
    event.preventDefault();
  }

  return (
    <div ref={rootRef} className={[styles.root, open && styles.open, className].filter(Boolean).join(" ")}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        // aria-disabled, а не disabled: фокус остаётся на кнопке, пока идёт выбранное действие.
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && setOpen((value) => !value)}
      >
        {trigger}
      </button>
      {open && (
        <ul id={menuId} role="menu" aria-label={label} className={styles.menu} onKeyDown={onKeyDown}>
          {items.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={item.tone === "danger" ? `${styles.item} ${styles.danger}` : styles.item}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
